import { makeAutoObservable, observable, runInAction } from "mobx";
import { ApiRequestError, type ApiClient } from "../platform/api-client";
import {
  permissionCodeSet,
  type ShellPermissionCode,
} from "./authorization/permission-codes";
import { detectClientDevice, primaryScreenForDevice } from "./client-device";
import type { ViewDeviceType } from "./feature-registry/feature-contract";

export type { ShellPermissionCode } from "./authorization/permission-codes";

export type SessionStatus = "anonymous" | "checking" | "authenticated";
export type PermissionDiscoveryStatus = "unavailable" | "ready";
/**
 * Lifecycle of the one-shot `GET /auth/sessions/current` bootstrap. `idle`
 * before it runs, `loading` while in flight, `ready` once the outcome is known
 * (authenticated OR anonymous), `error` only for a network/5xx failure that the
 * user can retry — a 401 is a normal anonymous outcome, never an error.
 */
export type SessionBootstrapStatus = "idle" | "loading" | "ready" | "error";

export interface PermissionDiscoverySnapshot {
  readonly activeOrganizationId: string;
  readonly allowedLayoutIds: readonly string[];
  readonly allowedScreenIds: readonly string[];
  readonly allowedViewIds: readonly string[];
  readonly authorizationVersion: string;
  readonly effectiveRoleIds: readonly string[];
  readonly featureFlags: Readonly<Record<string, boolean>>;
  readonly permissions: readonly string[];
  readonly status: "authenticated";
}

/** One AUTH-017 4-tuple grant, as `GET /auth/sessions/current` reports it. */
export interface SessionGrantView {
  readonly permission: string;
  readonly scopeType: string;
  readonly scopeRef: string | null;
  readonly includesSubtree: boolean;
}

/** One organization directly selectable within a grant's own roots (multi-OV work context). */
export interface SelectableOrganizationView {
  readonly unitId: string;
  readonly label: string;
  readonly unitTypeCode: string;
}

/** One AUTH-021 selectable context (an effective technical grant). */
export interface SessionContextView {
  readonly grantId: string;
  readonly roleCode: string;
  readonly organizationScopeUnitIds: readonly string[];
  readonly missionScopeIds: readonly string[];
  readonly effectiveFrom: string;
  readonly effectiveTo: string | null;
  readonly isActive: boolean;
  /**
   * Directly selectable organizations among this grant's own roots. Empty
   * when none of the roots is itself a permitted working-context type (e.g. a
   * Regionalstelle- or THW-root grant) — the UI must then browse via
   * `loadSelectableOrganizations` instead of expecting this list to be complete.
   */
  readonly organizations: readonly SelectableOrganizationView[];
}

export interface SessionIdentity {
  readonly principalId: string;
  readonly displayName: string;
  readonly personId: string | null;
  readonly avatarKey?: string | null;
  /**
   * Self-owned structural personnel function for presentation/signatures.
   * It is not a technical access role and grants no operational authority.
   */
  readonly personnelFunction?: {
    readonly code: string;
    readonly label: string;
    readonly source: "PERSONNEL_ASSIGNMENT";
  } | null;
  /** The account's home/administrative organization. Never changed by a context switch. */
  readonly homeOrganizationUnit: { readonly id: string; readonly name: string | null };
}

export interface ActiveWorkContext {
  readonly grantId: string;
  readonly roleCode: string;
  readonly organizationUnit: { readonly id: string; readonly name: string | null };
}

/** The exact shape of `GET /api/v1/auth/sessions/current` (F1). */
export interface CurrentSessionView {
  readonly principalId: string;
  readonly displayName: string;
  readonly personId: string | null;
  readonly avatarKey?: string | null;
  readonly personnelFunction?: {
    readonly code: string;
    readonly label: string;
    readonly source: "PERSONNEL_ASSIGNMENT";
  } | null;
  readonly homeOrganizationUnit: { readonly id: string; readonly name: string | null };
  readonly activeContext: ActiveWorkContext | null;
  readonly grants: readonly SessionGrantView[];
  readonly permissions: readonly string[];
  readonly availableContexts: readonly SessionContextView[];
  readonly session: { readonly expiresAt: string; readonly version: number };
  /** Fresh session-bound CSRF token so a reloaded, cookie-only client can mutate again. */
  readonly csrfToken?: string;
}

/**
 * Multi-role demo and production accounts must enter through their operational
 * home function, not through whichever grant UUID happens to sort first.
 * Secondary grants remain selectable in the header.
 */
const DEFAULT_CONTEXT_ROLE_PRIORITY: Readonly<Record<string, number>> = Object.freeze({
  super_admin: 1_000,
  lv_admin: 950,
  rst_admin: 900,
  ov_admin: 850,
  luk_dispatcher: 800,
  regionalstelle: 780,
  incident_commander: 760,
  s1_personnel: 750,
  s2_situation_reporting: 750,
  s3_operations: 750,
  s4_logistics: 750,
  s6_communications: 750,
  etb_keeper: 750,
  technical_adviser: 740,
  unit_leader: 730,
  field_user: 720,
  master_data_admin: 700,
  form_approver: 690,
  registration_officer: 680,
});

function defaultContextRolePriority(roleCode: string): number {
  return DEFAULT_CONTEXT_ROLE_PRIORITY[roleCode] ?? 0;
}

const GLOBAL_ADMINISTRATOR_ROLE_CODES = new Set([
  "superuser",
  "super_admin",
  "access_administrator",
]);

const SESSION_PATH = "/v1/auth/sessions";
const CURRENT_PATH = `${SESSION_PATH}/current`;
const ACTIVE_CONTEXT_PATH = `${CURRENT_PATH}/active-context`;
const PASSWORD_PATH = `${CURRENT_PATH}/password`;
const SELECTABLE_ORGANIZATIONS_PATH = `${CURRENT_PATH}/selectable-organizations`;

function capabilitiesPath(organizationId: string, device: ViewDeviceType): string {
  const query = new URLSearchParams({
    device,
    organizationId,
    screen: primaryScreenForDevice(device),
  });
  return `${CURRENT_PATH}/capabilities?${query.toString()}`;
}

/**
 * Authoritative client view of the caller's session. Everything the shell needs
 * to decide what to render — identity, the narrowed permission set of the
 * *active* context, and the other contexts the principal could switch to.
 *
 * Backend authorization is still enforced independently on every request; this
 * store only governs discovery and navigation.
 */
export class SessionStore {
  status: SessionStatus = "anonymous";
  bootstrapStatus: SessionBootstrapStatus = "idle";
  permissionDiscovery: PermissionDiscoveryStatus = "unavailable";
  permissions = observable.set<ShellPermissionCode>();
  allowedViewIds = observable.set<string>();
  allowedScreenIds = observable.set<string>();
  allowedLayoutIds = observable.set<string>();
  effectiveRoleIds = observable.set<string>();
  featureFlags = observable.map<string, boolean>();
  activeOrganizationId: string | null = null;
  authorizationVersion: string | null = null;
  csrfToken: string | null = null;
  identity: SessionIdentity | null = null;
  activeContext: ActiveWorkContext | null = null;
  availableContexts: readonly SessionContextView[] = [];
  /** `session.version`: the `If-Match` precondition for switching or logging out. */
  sessionVersion: number | null = null;
  /** True while a context switch is round-tripping; drives the switcher busy state. */
  contextSwitching = false;

  clientDevice: ViewDeviceType;

  constructor(clientDevice: ViewDeviceType = detectClientDevice()) {
    this.clientDevice = clientDevice;
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get isDesktopWebClient(): boolean { return this.clientDevice === "desktop"; }

  /** The concrete organization the account is currently working in, if any. Show this on every M02-M07 page. */
  get activeOrganizationUnit(): { readonly id: string; readonly name: string | null } | null {
    return this.activeContext?.organizationUnit ?? null;
  }

  get isGlobalAdministrationContext(): boolean {
    if (
      this.activeContext === null ||
      !GLOBAL_ADMINISTRATOR_ROLE_CODES.has(this.activeContext.roleCode)
    ) return false;
    const context = this.availableContexts.find(
      (candidate) => candidate.grantId === this.activeContext?.grantId,
    );
    return context?.organizationScopeUnitIds.includes(
      this.activeContext.organizationUnit.id,
    ) ?? false;
  }

  /** Re-classifies the client device after a live viewport change. See `use-responsive-device.ts`. */
  setClientDevice(device: ViewDeviceType): void {
    this.clientDevice = device;
  }

  private validatedPermissions(permissions: readonly string[]): ShellPermissionCode[] {
    const validated = permissions.filter(
      (permission): permission is ShellPermissionCode => permissionCodeSet.has(permission),
    );
    if (validated.length !== permissions.length) {
      this.clear();
      throw new Error("INVALID_PERMISSION_SNAPSHOT");
    }
    return validated;
  }

  /**
   * Narrow an incoming permission snapshot to the codes this client understands.
   *
   * Forward-compatibility rule: an unknown code (the backend added a permission
   * this build predates) is IGNORED with a warning, never a hard failure. The
   * old behaviour cleared the whole session and threw, which would lock a user
   * out of everything the moment the API grew one new code. Known codes still
   * gate exactly as before.
   */
  applyDiscoverySnapshot(snapshot: PermissionDiscoverySnapshot): void {
    const permissions = this.validatedPermissions(snapshot.permissions);
    this.status = snapshot.status;
    // A normalized capability snapshot is the final authorization bootstrap
    // result, not an intermediate session state. Keep the two lifecycle flags
    // consistent so injected/embedded stores and the production capability
    // refresh cannot remain trapped behind SessionGate's loading boundary.
    this.bootstrapStatus = "ready";
    this.permissions.replace(permissions);
    this.allowedViewIds.replace(snapshot.allowedViewIds);
    this.allowedScreenIds.replace(snapshot.allowedScreenIds);
    this.allowedLayoutIds.replace(snapshot.allowedLayoutIds);
    this.effectiveRoleIds.replace(snapshot.effectiveRoleIds);
    this.featureFlags.replace(snapshot.featureFlags);
    this.activeOrganizationId = snapshot.activeOrganizationId;
    this.authorizationVersion = snapshot.authorizationVersion;
    this.permissionDiscovery = "ready";
  }

  /** Populate the full session from a `GET /auth/sessions/current` response. */
  applySession(view: CurrentSessionView): void {
    const permissions = this.validatedPermissions(view.permissions);
    this.identity = {
      avatarKey: view.avatarKey ?? null,
      principalId: view.principalId,
      displayName: view.displayName,
      personId: view.personId,
      personnelFunction: view.personnelFunction ?? null,
      homeOrganizationUnit: view.homeOrganizationUnit,
    };
    this.activeContext = view.activeContext;
    this.availableContexts = view.availableContexts;
    this.sessionVersion = view.session.version;
    this.bootstrapStatus = "ready";
    this.status = "authenticated";
    this.permissions.replace(permissions);
    this.permissionDiscovery = "ready";
    // The current-session endpoint does not return the effective view/layout
    // contract. Clear any prior-context capability data until the dedicated
    // capabilities endpoint refreshes it, avoiding stale authorization hints.
    this.allowedViewIds.clear();
    this.allowedScreenIds.clear();
    this.allowedLayoutIds.clear();
    this.effectiveRoleIds.clear();
    this.featureFlags.clear();
    // Multi-OV: capability discovery must key off the ACTIVE organization, not
    // the account's home organization — a multi-OV grant's home org is not
    // necessarily the org currently being worked in.
    this.activeOrganizationId = view.activeContext?.organizationUnit.id ?? null;
    this.authorizationVersion = null;
    // `GET current` returns the session-bound CSRF token; adopt it so a
    // reloaded, cookie-only session can mutate without signing in again. The
    // server keeps it stable across tabs sharing the same session cookie.
    if (view.csrfToken !== undefined) this.setCsrfToken(view.csrfToken);
  }

  setCsrfToken(token: string | null): void {
    this.csrfToken = token;
  }

  clear(): void {
    this.status = "anonymous";
    this.permissionDiscovery = "unavailable";
    this.permissions.clear();
    this.allowedViewIds.clear();
    this.allowedScreenIds.clear();
    this.allowedLayoutIds.clear();
    this.effectiveRoleIds.clear();
    this.featureFlags.clear();
    this.activeOrganizationId = null;
    this.authorizationVersion = null;
    this.csrfToken = null;
    this.identity = null;
    this.activeContext = null;
    this.availableContexts = [];
    this.sessionVersion = null;
    this.contextSwitching = false;
  }

  /**
   * One-shot session bootstrap. On 200 the store becomes authenticated and its
   * permission set is populated; on 401 it becomes anonymous (the shell shows
   * the login view); any other failure surfaces a retryable error state.
   *
   * `applySession` also adopts the session-bound `csrfToken` that
   * `GET current` re-issues, so a reloaded, cookie-only session can resume
   * mutations without signing in again.
   */
  async bootstrap(api: ApiClient): Promise<void> {
    if (this.bootstrapStatus === "loading") return;
    this.bootstrapStatus = "loading";
    this.status = "checking";
    try {
      const view = await api.get<CurrentSessionView>(CURRENT_PATH);
      runInAction(() => {
        this.applySession(view);
        // AUTH-021 deliberately creates sessions without an active grant. In
        // that state the authenticated shell must render the context selector;
        // capability discovery is meaningful only after the operator selects
        // one of the available contexts.
        if (view.activeContext === null) {
          this.permissionDiscovery = "unavailable";
          // `applySession` marks the identity authenticated/ready. Keep the
          // gate in its loading state while the deterministic default context
          // is selected, otherwise the routed shell mounts for one render
          // without any entitled views and can persist a role-inappropriate
          // workspace/header.
          this.status = "checking";
          this.bootstrapStatus = "loading";
          return;
        }
        // Do not expose a half-authorized shell while the normalized view and
        // layout contract is still loading.
        this.status = "checking";
        this.bootstrapStatus = "loading";
        this.permissionDiscovery = "unavailable";
      });
      if (view.activeContext === null) {
        // Establish a deterministic default through the server-validated flow
        // used by explicit organization changes.
        const defaultContext = await this.resolveDefaultContext(api, view);
        if (defaultContext !== null) {
          await this.switchContext(
            api,
            defaultContext.grantId,
            defaultContext.organizationUnitId,
          );
          runInAction(() => {
            this.bootstrapStatus = "ready";
          });
        } else {
          // No deterministic default exists (for example a deliberately
          // unscoped account). Finish the bootstrap so the authenticated shell
          // can present its explicit work-context selector.
          runInAction(() => {
            this.status = "authenticated";
            this.bootstrapStatus = "ready";
          });
        }
        return;
      }
      await this.refreshCapabilities(api);
      runInAction(() => {
        this.bootstrapStatus = "ready";
      });
    } catch (error) {
      runInAction(() => {
        this.clear();
        this.bootstrapStatus =
          error instanceof ApiRequestError && error.status === 401 ? "ready" : "error";
      });
    }
  }

  setOwnAvatarKey(avatarKey: string): void {
    if (this.identity === null) return;
    this.identity = { ...this.identity, avatarKey };
  }

  /**
   * Select the highest available organization automatically. Direct grant
   * roots are already the highest selectable units for that grant. Root-scoped
   * grants (including Super Admin) use the server-ranked descendant endpoint.
   */
  private async resolveDefaultContext(
    api: ApiClient,
    view: CurrentSessionView,
  ): Promise<{ readonly grantId: string; readonly organizationUnitId: string } | null> {
    const homeOrganizationId = view.homeOrganizationUnit.id;
    const contexts = [...view.availableContexts].sort((left, right) =>
      Number(right.organizationScopeUnitIds.includes(homeOrganizationId)) -
        Number(left.organizationScopeUnitIds.includes(homeOrganizationId)) ||
      defaultContextRolePriority(right.roleCode) -
        defaultContextRolePriority(left.roleCode) ||
      left.roleCode.localeCompare(right.roleCode) ||
      left.grantId.localeCompare(right.grantId));

    for (const context of contexts) {
      const homeOrganization = context.organizations.find(
        (organization) => organization.unitId === homeOrganizationId,
      );
      if (homeOrganization !== undefined) {
        return {
          grantId: context.grantId,
          organizationUnitId: homeOrganization.unitId,
        };
      }
      const organization = context.organizations[0];
      if (organization !== undefined) {
        return {
          grantId: context.grantId,
          organizationUnitId: organization.unitId,
        };
      }
      const result = await this.loadSelectableOrganizations(
        api,
        context.grantId,
        { limit: 1 },
      );
      const descendant = result.items[0];
      if (descendant !== undefined) {
        return {
          grantId: context.grantId,
          organizationUnitId: descendant.unitId,
        };
      }
    }

    return null;
  }

  /** Re-read the session so permissions re-narrow after a context change. */
  async refresh(api: ApiClient): Promise<void> {
    const view = await api.get<CurrentSessionView>(CURRENT_PATH);
    runInAction(() => {
      this.applySession(view);
      this.permissionDiscovery = "unavailable";
    });
    await this.refreshCapabilities(api);
  }

  /** Load the backend-normalized view/layout contract for the active context. */
  async refreshCapabilities(api: ApiClient): Promise<void> {
    if (this.activeOrganizationId === null) throw new Error("ACTIVE_ORGANIZATION_UNAVAILABLE");
    const snapshot = await api.get<PermissionDiscoverySnapshot>(
      capabilitiesPath(this.activeOrganizationId, this.clientDevice),
    );
    runInAction(() => {
      this.applyDiscoverySnapshot(snapshot);
    });
  }

  /**
   * AUTH-021 active-context selection: the grant AND the concrete organization
   * to work in, together. Switching only ever narrows authority to the
   * selected grant narrowed to the selected organization — it never widens it.
   * Sends the current `session.version` as `If-Match`, then refreshes so the
   * permission set reflects the new active context.
   */
  async switchContext(api: ApiClient, grantId: string, organizationUnitId: string): Promise<void> {
    if (this.sessionVersion === null) throw new Error("SESSION_VERSION_UNAVAILABLE");
    this.contextSwitching = true;
    try {
      await api.put<{ activeGrantId: string; activeOrganizationUnitId: string; version: number }>(
        ACTIVE_CONTEXT_PATH,
        { grantId, organizationUnitId },
        { ifMatch: this.sessionVersion },
      );
      await this.refresh(api);
    } finally {
      runInAction(() => {
        this.contextSwitching = false;
      });
    }
  }

  /**
   * Paginated, searchable descendants of one owned grant's roots, filtered to
   * permitted working-context types. Used by the work-context selector when a
   * grant's `organizations` list is empty (a root-level grant, e.g. a system
   * administrator's THW-root scope) instead of expecting bootstrap to have
   * enumerated every descendant.
   */
  async loadSelectableOrganizations(
    api: ApiClient,
    grantId: string,
    query?: { readonly q?: string; readonly limit?: number; readonly offset?: number },
  ): Promise<{ readonly items: readonly SelectableOrganizationView[]; readonly total: number }> {
    const params = new URLSearchParams({ grantId });
    if (query?.q !== undefined && query.q.length > 0) params.set("q", query.q);
    if (query?.limit !== undefined) params.set("limit", String(query.limit));
    if (query?.offset !== undefined) params.set("offset", String(query.offset));
    return api.get<{ items: readonly SelectableOrganizationView[]; total: number }>(
      `${SELECTABLE_ORGANIZATIONS_PATH}?${params.toString()}`,
    );
  }

  /** Revoke the session and return the shell to the login view. */
  async logout(api: ApiClient): Promise<void> {
    try {
      if (this.sessionVersion !== null) {
        await api.delete(CURRENT_PATH, undefined, { ifMatch: this.sessionVersion });
      }
    } finally {
      runInAction(() => {
        this.clear();
        this.bootstrapStatus = "ready";
      });
    }
  }

  /** Change the current account's credential. The server revokes every session, including this one. */
  async changePassword(api: ApiClient, currentPassword: string, newPassword: string): Promise<void> {
    await api.put<void>(PASSWORD_PATH, { currentPassword, newPassword });
    runInAction(() => {
      this.clear();
      this.bootstrapStatus = "ready";
    });
  }

  canDiscover(requiredPermissions: readonly ShellPermissionCode[]): boolean {
    return (
      this.status === "authenticated" &&
      this.permissionDiscovery === "ready" &&
      requiredPermissions.every((permission) => this.permissions.has(permission))
    );
  }

  canDiscoverAny(requiredPermissions: readonly ShellPermissionCode[]): boolean {
    return this.status === "authenticated" && this.permissionDiscovery === "ready" &&
      requiredPermissions.some((permission) => this.permissions.has(permission));
  }

  isViewAllowed(viewId: string): boolean {
    return this.status === "authenticated" &&
      this.permissionDiscovery === "ready" &&
      this.allowedViewIds.has(viewId);
  }
}
